// src/utils/tokenUtils.js
import { ethers } from "ethers";

// ERC20 Token ABI - minimal interface for balanceOf function
const TOKEN_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function name() view returns (string)"
];

const TOKEN_CONTRACT_ADDRESS = "0x9d27c4cf95fb475cb98cb00ce3d97a00b8a3176a";
const MIN_TOKEN_REQUIREMENT = 250000; // 250,000 tokens

/**
 * Check if a wallet has enough tokens to use agents
 * @param {string} walletAddress The wallet address to check
 * @param {ethers.BrowserProvider} provider The ethers provider
 * @returns {Promise<{hasEnoughTokens: boolean, balance: string, requiredAmount: string, tokenSymbol: string}>}
 */
export const checkTokenRequirement = async (walletAddress, provider) => {
  try {
    if (!walletAddress || !provider) {
      return {
        hasEnoughTokens: false,
        balance: "0",
        requiredAmount: MIN_TOKEN_REQUIREMENT.toLocaleString(),
        tokenSymbol: "tokens",
        error: "Wallet not connected"
      };
    }

    const tokenContract = new ethers.Contract(TOKEN_CONTRACT_ADDRESS, TOKEN_ABI, provider);
    
    // Get token details
    const [balance, decimals, symbol] = await Promise.all([
      tokenContract.balanceOf(walletAddress),
      tokenContract.decimals(),
      tokenContract.symbol()
    ]);

    // Convert balance to human-readable format (considering token decimals)
    const balanceNumber = parseFloat(ethers.formatUnits(balance, decimals));
    const formattedBalance = balanceNumber.toLocaleString(undefined, {
      maximumFractionDigits: 2
    });

    // Check if balance meets requirement
    const hasEnoughTokens = balanceNumber >= MIN_TOKEN_REQUIREMENT;

    return {
      hasEnoughTokens,
      balance: formattedBalance,
      requiredAmount: MIN_TOKEN_REQUIREMENT.toLocaleString(),
      tokenSymbol: symbol || "tokens",
      error: null
    };
  } catch (error) {
    console.error("Error checking token balance:", error);
    return {
      hasEnoughTokens: false,
      balance: "0",
      requiredAmount: MIN_TOKEN_REQUIREMENT.toLocaleString(),
      tokenSymbol: "tokens",
      error: `Failed to check token balance: ${error.message || "Unknown error"}`
    };
  }
};
